import React, { useState, useEffect } from 'react';
import withRouter from '../../../../services/util/withRouter';
import { withTranslation } from 'react-i18next';
import editorKeypads from '../../../../services/api/editor/editorKeypads';
import editorKeypadItems from '../../../../services/api/editor/editorKeypadItems';
import editorMachines from '../../../../services/api/editor/editorMachines';
import { argbIntToHex } from '../../../../services/util/colorUtils';
import { localeData } from 'moment';
import LCDComponent from './LCD/LCDComponent';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import {
	GreenAccessButton,
	RedAccessButton,
	DisabledAccessButton
} from '../../../../components/Machines/MachineComponents';
import VMUtil from '../../../../services/util/vmUtil';
import machineAPI from '../../../../services/api/machines';
import realtime from '../../../../services/api/realtime';
import classes from './EditorBasedKeypad.module.css';
import { PosAnimation } from 'leaflet';

const ItemTypeEnum = {
	LCD: 0,
	Label: 1,
	Button: 2,
	Led: 3
};

type KeyPadItem = {
	KeypadItemID: number,
	KeypadID: number,
	ItemTypeEnum: number,
	ButtonTypeEnum: number,
	PosX: number,
	PosY: number,
	Width: number,
	Height: number,
	ButtonID: number,
	Caption: string,
	TopCaption: string,
	BottomCaption: string,
	BackColor: number,
	ForeColor: number,
	ButtonLedColor: null | string,
	editable: boolean
};

type KeyPad = {
	KeypadID: number,
	KeypadName: string,
	KeypadTypeEnum: number,
	KeypadAspectWidth: number,
	KeypadAspectHeight: number,
	KeypadColumns: number,
	KeypadRows: number,
	editable: boolean
};

type MachineParameter = {
	parameterName: string,
	parameterValue: string
};

type EditorMachineDefinition = {
	Boilers: number,
	ButtonDelay: number,
	CapsuleTanks: number,
	Cooler: number,
	CupHolder: number,
	DisplayAspectRatioEnum: number,
	DisplayOrientation: number,
	DisplaySizeEnum: number,
	Grinders: number,
	ImageID: number,
	KeypadButtonIDList: String,
	KeypadButtons: String,
	KeypadID: number,
	KeypadTypeEnum: number,
	LcdHeight: number,
	LcdWidth: number,
	MachineCommID: string,
	MachineID: number,
	MachineLanguageEnum: number,
	MachineName: string,
	MasterSlaveTypeEnum: number,
	OwnerGroupID: number,
	PreselectionLamps: number,
	PreselectorColdWater: string,
	PreselectorColdWaterDefault: number,
	PreselectorColdWaterMax: number,
	PreselectorColdWaterMin: number,
	PreselectorColdWaterTypeEnum: number,
	PreselectorCup: string,
	PreselectorCupDefault: number,
	PreselectorCupMax: number,
	PreselectorCupMin: number,
	PreselectorCupTypeEnum: number,
	PreselectorHotWater: string,
	PreselectorHotWaterDefault: number,
	PreselectorHotWaterMax: number,
	PreselectorHotWaterMin: number,
	PreselectorHotWaterTypeEnum: number,
	PreselectorJug: string,
	PreselectorJugDefault: number,
	PreselectorJugMax: number,
	PreselectorJugMin: number,
	PreselectorJugTypeEnum: number,
	PreselectorMilk: string,
	PreselectorMilkDefault: number,
	PreselectorMilkMax: number,
	PreselectorMilkMin: number,
	PreselectorMilkTypeEnum: number,
	PreselectorStrength: string,
	PreselectorStrengthDefault: number,
	PreselectorStrengthMax: number,
	PreselectorStrengthMin: number,
	PreselectorStrengthTypeEnum: number,
	PreselectorSugar: string,
	PreselectorSugarDefault: number,
	PreselectorSugarMax: number,
	PreselectorSugarMin: number,
	PreselectorSugarTypeEnum: number,
	PreselectorWater: string,
	PreselectorWaterDefault: number,
	PreselectorWaterMax: number,
	PreselectorWaterMin: number,
	PreselectorWaterTypeEnum: number,
	ProductLampType: number,
	ProductLamps: number,
	ProgButton: number,
	ScreenRotationEnum: number,
	Slave0XX: number,
	Slave9XX: number,
	SlaveAXX: number,
	SlaveBXX: number,
	SlaveXX: number,
	SmallCupHolder: number,
	SnackColumns: number,
	SnackRows: number,
	Tanks: number,
	VendingTypeEnum: number,
	VendorID: number,
	editable: boolean
};

type Props = {
	prefix: String,
	
	editorMachineId: number,
	vmID: string,
	history: Object,
	products?: Array<Object>,
	remoteControlEnabled: boolean,
	onRemoteControlSwitched: (remoteControl: boolean) => void
};

type States = {	
	keypad: KeyPad,
	keypadItems: KeyPadItem[],
	machineTitle: string,
	showLCD: boolean,
	hardwareID: string,
	LCDLines: string[],
	LCDInvertedMap: boolean[],
	controllerWidth: number,
	machine: EditorMachineDefinition,
};

const DEFAULT_LINE_NUMBER = 4;
const DEFAULT_CONTROLLER_WIDTH = 400;
const LCD_PADDING = Math.ceil(6 * 2); //PX	
const LCD_BLOCK_WIDTH = 32; //PX
const PADDING_BETWEEN_BUTTONS = Math.ceil(3.2 * 2); //px


class EditorBasedKeypad extends React.Component<Props, States> {
	listeners: Array<Subscription> = [];
	// We can not safely handle the LCD lines through
	// the state, because the updates can be very frequent and it would cause performance issues	
	lcdLineBuffer: string[] = [];
	lcdInvertedMap: boolean[] = [];

	constructor(props) {
		super(props);
	}

	componentWillUnmount = async () => {		
		await Promise.all([realtime.turnOffLCD(this.state.hardwareID), realtime.turnOffRemoteControl(this.state.hardwareID)]);		
		this.listeners.forEach(listener => listener.unsubscribe());
	};

	async componentDidMount() {
		const vmID = this.props.vmID
			? this.props.vmID
			: VMUtil.decodeVMID(this.props.match.params.vmID);

		
		const hardwareIDS = await machineAPI.getHardwareIDSByMachineID(vmID);
		const hardwareID = hardwareIDS.length > 0 ? hardwareIDS[0] : null;
		
		this.setState({ hardwareID: hardwareID });
	
		// Subscribe to realtime updates for the LCD and buttons
		this.listeners.push(
			await realtime.subscribeLCDForVM(hardwareID, (message) => this.handleLcdMessage(message))
		);
		this.listeners.push(
			await realtime.subscribeForSugarLamps(
				hardwareID,
				(message) => this.handleSugarLampsMessage(message)
			)
		)
		this.listeners.push(
			await realtime.subscribeLCDTimeoutForVM(
				hardwareID,
				() => this.handleLcdTimeoutMessage()
			)
		);


		await realtime.turnOnLCD(hardwareID);
		this.setState({ showLCD: true });
		
		this.loadMachineDefinitions();
	}

	async componentDidUpdate(prevProps, prevState) {		
		if (!this.props.editorMachineId || this.props.editorMachineId ===prevProps.editorMachineId) {
			return;
		}
		this.loadMachineDefinitions();
	}

	loadMachineDefinitions() {		
		editorMachines.getMachineById(this.props.editorMachineId).then(response => {			
			const machine = response.data && response.data.length > 0 ? response.data[0] : null;			
			if (!machine) {
				return;
			}
			
			this.setState({
				machine,
				machineTitle: this.props.prefix
					? `${this.props.prefix} - ${machine.MachineName}`
					: machine.MachineName,
				LCDLines: Array(machine && machine.LcdHeight ? machine.LcdHeight : DEFAULT_LINE_NUMBER).fill(''),			
				LCDInvertedMap: Array(machine && machine.LcdHeight ? machine.LcdHeight : DEFAULT_LINE_NUMBER).fill(false)
			});
			this.lcdLineBuffer = Array(machine && machine.LcdHeight ? machine.LcdHeight : DEFAULT_LINE_NUMBER).fill('');
			this.lcdInvertedMap = Array(machine && machine.LcdHeight ? machine.LcdHeight : DEFAULT_LINE_NUMBER).fill(false);

			editorKeypads.getKeyPadById(machine.KeypadID).then(response => {
				this.setState({ keypad: response.data[0] });
				editorKeypadItems
					.getKeyPadItemsByKeypadId(machine.KeypadID)
					.then(response => {
						const lcdWidth = machine?.LcdWidth ?? 0;
										
						// Calculate controller width based on LCD width and keypad columns
						const keypadItems = response.data;
						const lcd = keypadItems.find(item => item.ItemTypeEnum === ItemTypeEnum.LCD);
						const lcdColCount= lcd ? lcd.Width : 1;					
						const controllerWidth =
							lcdWidth > 0
								? Math.ceil(
										((lcdWidth * LCD_BLOCK_WIDTH + LCD_PADDING) / lcdColCount) *
											this.state.keypad.KeypadColumns
								) +
								Math.ceil(
										PADDING_BETWEEN_BUTTONS * this.state.keypad.KeypadColumns
								)
								: DEFAULT_CONTROLLER_WIDTH;
						
						this.setState({ keypadItems: response.data, controllerWidth });
					});
			});
		});
	}


	handleLcdMessage = (message: any) => {				
		const newMessage = message.toString();
		const lineNumber = this.lcdLineBuffer ? this.lcdLineBuffer.length : 0;
		let LCDLines = this.lcdLineBuffer && this.lcdLineBuffer.length > 0 ? [...this.lcdLineBuffer] : [];
		let LCDInvertedMap = this.lcdInvertedMap && this.lcdInvertedMap.length > 0 ? [...this.lcdInvertedMap] : [];
		
		let lcdLineChanged = false;
		for (let i = 0; i < lineNumber; i++) {
			if (newMessage.indexOf(`LINE${i + 1}=`) !== -1) {		
				// Decode hex to ASCII
				let {line, invertedMap} = VMUtil.decodeLCDLine(newMessage.replace(`LINE${i + 1}=`, ''), this.props.machineType);								
				LCDLines[i] = line;
				LCDInvertedMap[i] = invertedMap;
				lcdLineChanged=true
			}
		}
		if (lcdLineChanged) {
			this.lcdLineBuffer = LCDLines;
			this.lcdInvertedMap = LCDInvertedMap;
			this.setState({
				LCDLines,
				LCDInvertedMap
			});
		}

		if (newMessage.indexOf(`LAMPS`) !== -1) {
			const ledStatusesString = newMessage.replace(`LAMPS=`, '');
			const ledStatuses = ledStatusesString
				.split('')
				.map(ledStatus => ledStatus === '1');
			this.setState({ ledStatuses });
		}
	};

	handleSugarLampsMessage = (message: any) => {		
		const newMessage = message.toString();

		// The order of the lamps is not determined, 
		// so the number of active lamps is counted and the first N lamps are turned on, where N is the number of active lamps
		const ledStatuses=newMessage.split('').map(() => false);
		const numberOfActiveLeds = newMessage.split('').filter((lampStatus, index) => lampStatus === '1');
		for(let i=0; i<numberOfActiveLeds.length; i++) {
			ledStatuses[i]=true;
		}
		this.setState({ ledStatuses });
	}


	handleLcdTimeoutMessage = () => {
		this.setState({ showLCD: false, remoteAcccess: false });
		realtime.turnOffRemoteControl(this.state.hardwareID);
		if (this.props.onRemoteControlSwitched) {
			this.props.onRemoteControlSwitched(false);
		}
	};

	handleButtonPress = (buttonId: string) => async () => {
		if (this.props.remoteControlEnabled) {
			await realtime.pressButton(
				this.state.hardwareID,
				buttonId,
				this.props.prefix ? this.props.prefix : ''
			);
		}
	};

	onSwitchLCD() {
		this.setState({ showLCD: !this.state.showLCD }, async () => {
			if (this.state.showLCD) {
				await realtime.turnOnLCD(this.state.hardwareID);
			} else {
				await realtime.turnOffLCD(this.state.hardwareID);
				await realtime.turnOffRemoteControl(this.state.hardwareID);
				this.setState({ remoteAcccess: false });

				if (this.props.onRemoteControlSwitched) {
					this.props.onRemoteControlSwitched(this.state.remoteAcccess);
				}
			}
		});
	}

	onSwitchRemoteControl() {
		this.setState({ remoteAcccess: !this.state.remoteAcccess }, async () => {
			if (this.state.remoteAcccess) {
				this.setState({ showLCD: true });
				await realtime.turnOnLCD(this.state.hardwareID);
				await realtime.turnOnRemoteControl(this.state.hardwareID);
				if (this.props.onRemoteControlSwitched) {
					this.props.onRemoteControlSwitched(this.state.remoteAcccess);
				}
			} else {
				await realtime.turnOffRemoteControl(this.state.hardwareID);
				if (this.props.onRemoteControlSwitched) {
					this.props.onRemoteControlSwitched(this.state.remoteAcccess);
				}
			}
		});
	}

	getProductForKeyPadItem(keyPadItem) {		
		return this.props.products?.find(product => product.productID == keyPadItem.ButtonID 
			&& ((!product.slavePattern && !this.props.prefix) 
			|| ( String(product.slavePattern).toLocaleUpperCase()==this.props.prefix.toLocaleUpperCase())));
		
	}

	getKeyPadItem(keyPadItem) {
		switch (keyPadItem.ItemTypeEnum) {
			case ItemTypeEnum.LCD:
				return !this.props.prefix ? (
					<div style={{position: 'relative'}}>
						<LCDComponent
							lcdWidth={this.state.machine.LcdWidth}
							lcdHeight={this.state.machine.LcdHeight}
							backColor={argbIntToHex(keyPadItem.BackColor)}
							foreColor={argbIntToHex(keyPadItem.ForeColor)}
							lines={this.state.LCDLines ?? []}
							invertedMap={this.state.LCDInvertedMap ?? []}
						></LCDComponent>
						{(!this.state.showLCD) ? (										
							<div className={classes.Overlay}></div>							
						) : null}
					</div>
				) : null;
			case ItemTypeEnum.Label:				
				const product = this.getProductForKeyPadItem(keyPadItem);
				return (
					<>
						<div
							className={classes.labeldiv}
							style={{
								backgroundColor: argbIntToHex(keyPadItem.BackColor),
								color: argbIntToHex(keyPadItem.ForeColor)
							}}
						>
							{product?.name}
						
							{product?.price && (
								<div style={{ fontSize: '0.7em' }}>
									{product?.price} {product?.currency}
								</div>
							)}
							{(!this.props.remoteControlEnabled) ? (				
								
									<div className={classes.Overlay}></div>
								
							) : null}
						</div>
					</>
				);
			case ItemTypeEnum.Button:
				return (
					<>
						<div
							className={classes.buttondiv}
							style={{
								backgroundColor: argbIntToHex(keyPadItem.BackColor),
								color: argbIntToHex(keyPadItem.ForeColor)
							}}
							onClick={this.handleButtonPress(keyPadItem.ButtonID.toString())}
						>
							{keyPadItem.Caption}
							{keyPadItem.TopCaption && (
								<div style={{ fontSize: '0.7em' }}>{keyPadItem.TopCaption}</div>
							)}
							{keyPadItem.BottomCaption && (
								<div style={{ fontSize: '0.7em' }}>
									{keyPadItem.BottomCaption}
								</div>
							)}
							{(!this.props.remoteControlEnabled) ? (												
								<div className={classes.Overlay}></div>								
							) : null}
						</div>
					</>
				);
			case ItemTypeEnum.Led:
				return (		
					<>
					
							{this.getLedComponent(keyPadItem)}
					
					</>
				);
			default:
				return (
					<>
						<div>Unknown</div>
					</>
				);
		}
	}

	getLedComponent(keyPadItem) {
		const ledStatuses = this.state.ledStatuses ?? [];
		return (
			<div className={classes.ledContainer} 
					style=
						{{
							backgroundColor: argbIntToHex(keyPadItem.BackColor),
							color: argbIntToHex(keyPadItem.ForeColor)
							}}
			>
				{ledStatuses.map((isOn, index) => (
					<div
						key={index}
						className={isOn ? classes.ledFilled : classes.ledEmpty}
						
					></div>
				))}
			</div>
		);
	}

	isInColOrRowSpan(keypadItems, rowIndex, columnIndex) {
		for (const item of keypadItems) {
			if (
				rowIndex >= item.PosY &&
				rowIndex < item.PosY + item.Height &&
				columnIndex >= item.PosX &&
				columnIndex < item.PosX + item.Width
			) {
				return true;
			}
		}
		return false;
	}

	getKeyPadRow(keypadItems, rowIndex, columnCount) {
		const result = [];

		for (let j = 0; j < columnCount; j++) {
			const keyPadItem = keypadItems.find(
				item => item.PosY == rowIndex && item.PosX == j
			);

			if (keyPadItem) {
				result.push(
					<td
						key={`${
							this.props.prefix ? this.props.prefix : 'master'
						}-${rowIndex}-${j}`}
						colSpan={keyPadItem.Width}
						rowSpan={keyPadItem.Height}
					>
						{this.getKeyPadItem(keyPadItem)}
					</td>
				);
			} else {
				if (this.isInColOrRowSpan(keypadItems, rowIndex, j)) {
					continue; // Skip cells covered by colspan
				}
				result.push(
					<td
						key={`${
							this.props.prefix ? this.props.prefix : 'master'
						}-${rowIndex}-empty-${j}`}
					></td>
				);
			}
		}
		return result;
	}

	render() {
		const { keypadItems, keypad } = this.state || {};

		if (!keypadItems || keypadItems.length === 0 || !keypad)
			return <></>;

		const rows = [];
		for (let i = 0; i < keypad.KeypadRows; i++) {
			rows.push(
				<tr key={`${this.props.prefix ? this.props.prefix : 'master'}-${i}`}>
					{this.getKeyPadRow(keypadItems, i, keypad.KeypadColumns)}
				</tr>
			);
		}

		const headerButtons = (
			<Col md="7" xs="12" style={{ textAlign: 'center' }} className={classes.headerbuttons}>
				{this.state.showLCD ? (
					<GreenAccessButton onClick={() => this.onSwitchLCD()}>
						{this.props.t('remoteControl.showLCD') +
							': ' +
							this.props.t('remoteControl.on')}
					</GreenAccessButton>
				) : (
					<RedAccessButton onClick={() => this.onSwitchLCD()}>
						{this.props.t('remoteControl.showLCD') +
							': ' +
							this.props.t('remoteControl.off')}
					</RedAccessButton>
				)}
				{this.props.remoteControlServiceEnabled ? (
					<>
						{this.state.remoteAcccess ? (
							<GreenAccessButton onClick={() => this.onSwitchRemoteControl()}>
								{this.props.t('remoteControl.remoteControl') +
									': ' +
									this.props.t('remoteControl.on')}
							</GreenAccessButton>
						) : (
							<RedAccessButton onClick={() => this.onSwitchRemoteControl()}>
								{this.props.t('remoteControl.remoteControl') +
									': ' +
									this.props.t('remoteControl.off')}
							</RedAccessButton>
						)}
					</>
				) : (
					<DisabledAccessButton onClick={() => this.showConfirmation()}>
						{this.props.t('remoteControl.remoteControl') +
							': ' +
							this.props.t('remoteControl.off')}
					</DisabledAccessButton>
				)}
			</Col>
		);

		const header = (
			<Row>
				<Col md="4" xs="12">
					<div className={classes.MachineTitle}>{this.state.machineTitle}</div>
				</Col>
				<Col
					md="1"
					xs="12"
					style={{ textAlign: 'right', minHeight: '1rem' }}
				></Col>
				{!this.props.prefix ? headerButtons : null}
			</Row>
		);

		const keyboardTable = <table className={classes.keyboardtable} style={{ width: '100%' }}><tbody>{rows}</tbody></table>;

		return (
			<div className={classes.ControlContainer}>
				{header}
				<div className={classes.KeyboardContainer} style={{ width: Math.max(this.state.controllerWidth ? this.state.controllerWidth : DEFAULT_CONTROLLER_WIDTH,  DEFAULT_CONTROLLER_WIDTH)}}>
					
				{keyboardTable}
				</div>
			</div>
		);
	}
}

export default withTranslation()(withRouter(EditorBasedKeypad));
